import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';

import {
  GetExteriorAssessmentData,
  GetExteriorAssessmentDataVariables,
} from './__generated__/GetExteriorAssessmentData';

/**
 * Number of days by which Home Assessments are valid
 * This comes from the backend https://github.com/opendoor-labs/web/blob/master/app/models/repair_budget_review.rb#L9
 */
export const INITIAL_INSPECTION_VALID_DAYS = 180;

/** Possible home assessment states */
export enum HomeAssessmentState {
  NOT_SCHEDULED,
  SCHEDULED,
  IN_PROGRESS,
  COMPLETED,
}

export const HomeAssessmentStateDisplayString: { [key in HomeAssessmentState]: string } = {
  [HomeAssessmentState.NOT_SCHEDULED]: 'Not Scheduled',
  [HomeAssessmentState.SCHEDULED]: 'Scheduled',
  [HomeAssessmentState.IN_PROGRESS]: 'In Progress',
  [HomeAssessmentState.COMPLETED]: 'Completed',
};

export const GET_EXTERIOR_ASSESSMENT_DATA = gql`
  query GetExteriorAssessmentData($sellerInputUUID: String!) {
    web {
      getAllExteriorAssessmentSlots(sellerInputUUID: $sellerInputUUID) {
        currentInspectionTime
        visitId
      }
    }
  }
`;

export const useGetExteriorAssessmentData = (sellerInputUUID?: string) => {
  const { data, error, loading } = useQuery<
    GetExteriorAssessmentData,
    GetExteriorAssessmentDataVariables
  >(GET_EXTERIOR_ASSESSMENT_DATA, {
    context: {
      clientName: 'athena',
    },
    variables: { sellerInputUUID: sellerInputUUID ?? '' },
    skip: !sellerInputUUID || !sellerInputUUID?.length,
  });

  return { data, error, loading };
};

/** Checks if exterior assessment is still valid */
export const isExteriorAssessmentValid = (inspectionDate: Date, currentDate: Date) => {
  const currentMomentDate = moment(currentDate);
  const validityCutoffMomentDate = currentMomentDate
    .clone()
    .subtract(INITIAL_INSPECTION_VALID_DAYS, 'days');
  const inspectionMomentDate = moment(inspectionDate);

  return (
    inspectionMomentDate.isAfter(validityCutoffMomentDate) &&
    // make sure inspection date is in the past
    inspectionMomentDate.isBefore(currentMomentDate)
  );
};
// Based on https://github.com/opendoor-labs/code/blob/e67c0c680072c0941baaa16ef0937ad4d4dc48d4/js/packages/cake/src/sellDirect/helpers/homeAssessments.ts#L114
export const getHomeAssessmentData = (
  trippyInspection?: Date,
  offerInspectionAt?: Date,
): HomeAssessmentData => {
  const currentDateTime = new Date();

  // Use trippyInspection for in progress or upcoming inspections
  if (trippyInspection) {
    const endTime = new Date(trippyInspection);
    endTime.setHours(trippyInspection.getHours() + 4);

    if (currentDateTime > trippyInspection && currentDateTime < endTime) {
      return {
        stateString: HomeAssessmentStateDisplayString[HomeAssessmentState.IN_PROGRESS],
        scheduledOrCompletedDateTime: trippyInspection,
      };
    }

    // If the start timeSlot is in the future, return SCHEDULED
    // hasUpcomingEA checks that the trippyInspection is in the future
    if (Date.now() < trippyInspection.getTime()) {
      return {
        stateString: `${
          HomeAssessmentStateDisplayString[HomeAssessmentState.SCHEDULED]
        } for ${trippyInspection.toLocaleDateString()}`,
        scheduledOrCompletedDateTime: trippyInspection,
      };
    }
  }

  // If the trippyInspection is not in progress, and not scheduled for the future,
  // then it is in the past, at which point we should look at the offer.initialInspectionAt
  if (offerInspectionAt) {
    // If the offerInspectionAt is within the past INITIAL_INSPECTION_VALID_DAYS days, then it's been completed
    if (isExteriorAssessmentValid(offerInspectionAt, currentDateTime)) {
      return {
        stateString: `${
          HomeAssessmentStateDisplayString[HomeAssessmentState.COMPLETED]
        } on ${offerInspectionAt.toLocaleDateString()}`,
        scheduledOrCompletedDateTime: offerInspectionAt,
      };
    }
  }

  return { stateString: HomeAssessmentStateDisplayString[HomeAssessmentState.NOT_SCHEDULED] };
};

export type HomeAssessmentData = { stateString: string; scheduledOrCompletedDateTime?: Date };
